import { useMemo } from 'react';

import { useHRAuth } from 'src/auth/hooks/use-hr-context';
import {
  parseRole,
  type CanFn,
  type RoleAction,
  canFromCapabilities,
  type OrganizationRole,
} from 'src/auth/role-matrix';

// ----------------------------------------------------------------------

export type UseRoleResult = {
  /** The caller's role, as the API named it; null until known. */
  role: OrganizationRole | null;
  /** The capability names the API granted this session. */
  capabilities: readonly string[];
  /** True while the token exchange (and so the permissions) has not answered yet. */
  isLoading: boolean;
  /** `can('jobs.create')`: true only when the API listed that capability for this member. */
  can: CanFn;
  isAdministrator: boolean;
  isRecruiter: boolean;
  isHiringManager: boolean;
  isHiringTeam: boolean;
};

/**
 * The one place the UI asks "what may this person do". The answer is whatever the API returned with the token
 * (`Role` and `Capabilities`, refreshed by the provider); nothing is decided on the client. The API still
 * checks every call, so this only decides what to render.
 */
export function useRole(): UseRoleResult {
  const { Role, Capabilities, loadingHRAuth } = useHRAuth();

  const role = useMemo(() => parseRole(Role), [Role]);
  const capabilities = useMemo<readonly string[]>(
    () => (Array.isArray(Capabilities) ? Capabilities : []),
    [Capabilities]
  );
  const can = useMemo(() => canFromCapabilities(capabilities), [capabilities]);

  return useMemo(
    () => ({
      role,
      capabilities,
      isLoading: Boolean(loadingHRAuth) && role === null,
      can,
      isAdministrator: role === 'Administrator',
      isRecruiter: role === 'Recruiter',
      isHiringManager: role === 'HiringManager',
      isHiringTeam: role === 'HiringTeam',
    }),
    [role, capabilities, loadingHRAuth, can]
  );
}

/** Shorthand when a component only needs one answer. */
export function useCan(action: RoleAction): boolean {
  return useRole().can(action);
}
